import { TrendingUp, TrendingDown } from 'lucide-react';
import { ReactNode } from 'react';

import { cn } from '@/lib/utils';
import { Card, CardContent } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';

interface StatCardProps {
  title: string;
  value: number | string;
  change?: number;
  icon?: ReactNode;
  variant?: 'default' | 'primary';
  loading?: boolean;
  onClick?: () => void;
  subtitle?: string;
  className?: string;
  valueClassName?: string;
}

export function StatCard({
  title,
  value,
  change,
  icon,
  variant = 'default',
  loading = false,
  onClick,
  subtitle,
  className,
  valueClassName,
}: StatCardProps) {
  const isPrimary = variant === 'primary';

  return (
    <Card
      onClick={onClick}
      className={cn(
        'h-full transition-colors duration-200',
        isPrimary
          ? 'bg-primary border-transparent'
          : 'bg-card border-border',
        onClick && 'cursor-pointer',
        onClick && !isPrimary && 'hover:border-primary/30',
        className
      )}
      role={onClick ? 'button' : undefined}
      tabIndex={onClick ? 0 : undefined}
      aria-label={onClick ? `${title}: ${value}` : undefined}
    >
      <CardContent className="flex items-center gap-3 p-4">
        {icon && (
          <div
            className={cn(
              'flex h-8 w-8 shrink-0 items-center justify-center rounded-lg [&_svg]:h-4 [&_svg]:w-4',
              isPrimary
                ? 'bg-white/15 [&_svg]:text-white'
                : 'bg-muted [&_svg]:text-muted-foreground'
            )}
          >
            {icon}
          </div>
        )}
        <div className="min-w-0 flex-1">
          <p
            className={cn(
              'text-xs',
              isPrimary ? 'text-white/80' : 'text-muted-foreground'
            )}
          >
            {title}
          </p>
          <div className="flex items-baseline gap-2">
            <span
              className={cn(
                'text-xl font-bold',
                isPrimary ? 'text-white' : 'text-foreground',
                valueClassName
              )}
            >
              {loading ? <Skeleton className="h-6 w-12" /> : value}
            </span>
            {change !== undefined && (
              <span
                className="inline-flex items-center gap-0.5 rounded px-1.5 py-0.5 text-[11px] font-semibold"
                style={{
                  backgroundColor: change >= 0 ? 'rgba(16, 185, 129, 0.1)' : 'rgba(239, 68, 68, 0.1)',
                  color: change >= 0 ? '#10B981' : '#EF4444',
                }}
                aria-label={`Variação: ${change >= 0 ? 'aumento' : 'queda'} de ${Math.abs(change)}%`}
              >
                {change >= 0 ? (
                  <TrendingUp className="h-3 w-3" />
                ) : (
                  <TrendingDown className="h-3 w-3" />
                )}
                {`${change >= 0 ? '+' : ''}${change}%`}
              </span>
            )}
          </div>
          {subtitle && (
            <p
              className={cn(
                'text-[11px] mt-0.5',
                isPrimary ? 'text-white/60' : 'text-muted-foreground/70'
              )}
            >
              {subtitle}
            </p>
          )}
        </div>
      </CardContent>
    </Card>
  );
}

export default StatCard;
